import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';

import {
  buildSessionRequest,
  buildTileUrlTemplate,
  getGoogleTileSessionManager,
  type GoogleMapType,
  type GoogleTileSession,
} from '../../services/googleTiles/session';
import {
  createThrottledRunner,
  escapeAttribution,
  fetchViewportCopyright,
  replaceAttribution,
} from '../../services/googleTiles/attribution';

export const GOOGLE_TILES_CLASS_NAME = 'atlas-google-tiles';
export const GOOGLE_LOGO_ON_WHITE_URL = `${process.env.PUBLIC_URL ?? ''}/google-maps/GoogleMaps_Logo_WithLightOutline.svg`;
export const GOOGLE_LOGO_ON_NON_WHITE_URL = `${process.env.PUBLIC_URL ?? ''}/google-maps/GoogleMaps_Logo_WithDarkOutline.svg`;

const TILE_ERROR_FALLBACK_THRESHOLD = 6;
const SESSION_PROBE_INTERVAL_MS = 10_000;
const MAX_TIMER_MS = 2_147_483_647;

interface GoogleTileLayerProps {
  apiKey: string;
  mapType: GoogleMapType;
  extraAttribution?: string;
  onUnavailable?: (mapType: GoogleMapType) => void;
}

function createLogoControl(mapType: GoogleMapType): L.Control {
  const LogoControl = L.Control.extend({
    onAdd() {
      const container = L.DomUtil.create('div', 'atlas-google-logo');
      const img = L.DomUtil.create('img', '', container) as HTMLImageElement;
      img.src = mapType === 'satellite' ? GOOGLE_LOGO_ON_NON_WHITE_URL : GOOGLE_LOGO_ON_WHITE_URL;
      img.alt = 'Google Maps';
      img.setAttribute('aria-label', 'Google Maps');
      img.style.height = '18px';
      img.style.display = 'block';
      container.style.lineHeight = '0';
      container.style.marginBottom = '5px';
      container.style.pointerEvents = 'none';
      return container;
    },
  });
  return new LogoControl({ position: 'bottomleft' });
}

export default function GoogleTileLayer({ apiKey, mapType, extraAttribution, onUnavailable }: GoogleTileLayerProps) {
  const map = useMap();
  const { i18n } = useTranslation();
  const manager = useMemo(() => getGoogleTileSessionManager(apiKey), [apiKey]);
  const request = useMemo(() => buildSessionRequest(mapType, i18n?.language), [mapType, i18n?.language]);
  const [session, setSession] = useState<GoogleTileSession | null>(null);
  const tileErrorsRef = useRef(0);
  const lastProbeRef = useRef(Number.NEGATIVE_INFINITY);
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  const refreshSession = useCallback(() => {
    manager
      .refresh(request)
      .then(setSession)
      .catch(() => onUnavailableRef.current?.(mapType));
  }, [manager, request, mapType]);

  useEffect(() => {
    let cancelled = false;
    setSession(null);
    tileErrorsRef.current = 0;
    manager
      .getSession(request)
      .then((next) => {
        if (!cancelled) setSession(next);
      })
      .catch(() => {
        if (!cancelled) onUnavailableRef.current?.(mapType);
      });
    return () => {
      cancelled = true;
    };
  }, [manager, request, mapType]);

  useEffect(() => {
    if (!session) return undefined;
    const timer = setTimeout(refreshSession, Math.min(manager.msUntilRefresh(session), MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [session, manager, refreshSession]);

  const [attributionControl, setAttributionControl] = useState<L.Control.Attribution | null>(null);

  useEffect(() => {
    const ownControl = map.attributionControl ? null : L.control.attribution({ prefix: false }).addTo(map);
    const control = map.attributionControl ?? ownControl!;
    const previousPrefix = (control.options as L.Control.AttributionOptions).prefix;
    control.setPrefix(false);
    const logo = createLogoControl(mapType).addTo(map);
    const extra = replaceAttribution(control, null, extraAttribution ?? null);
    setAttributionControl(control);
    return () => {
      setAttributionControl(null);
      logo.remove();
      replaceAttribution(control, extra, null);
      if (ownControl) {
        ownControl.remove();
      } else if (typeof previousPrefix === 'string' || previousPrefix === false) {
        control.setPrefix(previousPrefix);
      }
    };
  }, [map, mapType, extraAttribution]);

  const runViewportCheck = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!session || !attributionControl) return undefined;
    let current: string | null = null;
    let disposed = false;

    const update = () => {
      const bounds = map.getBounds();
      void fetchViewportCopyright(apiKey, session.session, {
        zoom: map.getZoom(),
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      }).then((result) => {
        if (disposed) return;
        if (result.status === 'session-invalid') {
          refreshSession();
          return;
        }
        if (result.status === 'ok') {
          current = replaceAttribution(attributionControl, current, escapeAttribution(result.copyright) || null);
        }
      });
    };

    const runner = createThrottledRunner(update);
    runViewportCheck.current = update;
    const onMoveEnd = () => runner.request();
    map.on('moveend', onMoveEnd);
    runner.request();
    return () => {
      disposed = true;
      runner.cancel();
      map.off('moveend', onMoveEnd);
      runViewportCheck.current = () => undefined;
      replaceAttribution(attributionControl, current, null);
    };
  }, [map, apiKey, session, attributionControl, refreshSession]);

  const eventHandlers = useMemo(
    () => ({
      tileload: () => {
        tileErrorsRef.current = 0;
      },
      tileerror: () => {
        tileErrorsRef.current += 1;
        const now = Date.now();
        if (now - lastProbeRef.current >= SESSION_PROBE_INTERVAL_MS) {
          lastProbeRef.current = now;
          runViewportCheck.current();
        }
        if (tileErrorsRef.current === TILE_ERROR_FALLBACK_THRESHOLD) {
          onUnavailableRef.current?.(mapType);
        }
      },
    }),
    [mapType]
  );

  if (!session) return null;

  return (
    <TileLayer
      url={buildTileUrlTemplate(session.session, apiKey)}
      className={GOOGLE_TILES_CLASS_NAME}
      maxZoom={22}
      maxNativeZoom={22}
      eventHandlers={eventHandlers}
    />
  );
}
